import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { API_BASE } from '../apiConfig';

const PageContainer = styled.div`
  width: 100%;
  background: #ffffff;
`;

const HeroSection = styled.section`
  padding: 80px 100px 60px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 20px 40px;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const AppLogo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  img {
    max-width: 220px;
    height: auto;

    @media (max-width: 768px) {
      max-width: 180px;
    }
  }
`;

const Tagline = styled.p`
  font-size: 24px;
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.95);

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto 40px;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const AppStoreBadge = styled.a`
  display: inline-block;
  img {
    height: 50px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ComingSoonBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 30px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
`;

const ContentSection = styled.section`
  padding: 80px 100px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: #666;
  max-width: 700px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

//const VideoCard = styled.div`
//  margin: 0 auto 80px;
//  padding: 24px;
//  border-radius: 12px;
//  background: #fff;
//  box-shadow: 0 10px 20px rgba(0,0,0,.08);
//  border-left: 5px solid #5b4b8a;
//  max-width: 1200px;
//`;

//const VideoTitle = styled.h3`
//  font-size: 24px;
//  font-weight: 600;
//  color: #5b4b8a;
//  margin: 0 0 8px;
//`;

//const VideoSub = styled.p`
//  font-size: 16px;
//  color: #555;
//  margin: 0 0 16px;
//`;

//const FrameWrap = styled.div`
//  position: relative;
//  width: 100%;
//  max-width: 1280px;
//  aspect-ratio: 16 / 9;
//  margin: 0 auto;
//  iframe {
//    position: absolute;
//    inset: 0;
//    width: 100%;
//    height: 100%;
//    border: 0;
//    border-radius: 10px;
//  }
//`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 30px;
  border: 2px solid #f0f0f0;
  transition: all 0.3s ease;

  &:hover {
    border-color: #8ecf41;
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .icon {
    font-size: 36px;
    margin-bottom: 15px;
  }

  h3 {
    font-size: 20px;
    color: #453793;
    margin-bottom: 10px;
    font-weight: 600;
  }

  p {
    font-size: 15px;
    line-height: 1.6;
    color: #666;
    margin: 0;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 600px;
  margin: 0 auto 60px;
  padding: 0 60px;

  @media (max-width: 768px) {
    padding: 0 40px;
  }
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
`;

const Screenshot = styled(motion.div)`
  width: 100%;
  aspect-ratio: 9 / 16;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #f8f9fa;
    border-radius: 20px;
  }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: none;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 10;
  color: #453793;
  font-size: 20px;
  font-weight: bold;

  &:hover {
    background: #453793;
    color: white;
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  ${props => props.direction === 'left' ? 'left: 0;' : 'right: 0;'}

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 16px;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#453793' : '#ddd'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    background: ${props => props.active ? '#453793' : '#aaa'};
    transform: scale(1.2);
  }
`;

const ScreenshotCaption = styled.div`
  text-align: center;
  margin-top: 15px;
  color: #666;
  font-size: 15px;
  font-style: italic;
`;

const TechStackSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 40px;
  margin-bottom: 60px;

  h3 {
    font-size: 24px;
    color: #333;
    margin-bottom: 20px;
  }

  .stack-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
  }

  .tech-badge {
    padding: 10px 20px;
    background: white;
    border-radius: 6px;
    font-size: 14px;
    color: #555;
    font-weight: 500;
    border: 1px solid #e0e0e0;
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const WaitlistSection = styled.div`
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  color: white;

  h3 {
    font-size: 32px;
    margin-bottom: 15px;
    color: white;
  }

  p {
    font-size: 18px;
    margin-bottom: 30px;
    color: rgba(255, 255, 255, 0.9);
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;

    h3 {
      font-size: 24px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const WaitlistForm = styled.form`
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 15px 20px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  background: white;
  color: #333;

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  padding: 15px 40px;
  background-color: #8ecf41;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background-color: #72b237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(142, 207, 65, 0.3);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: rgba(142, 207, 65, 0.2);
  border: 1px solid rgba(142, 207, 65, 0.5);
  border-radius: 6px;
  color: white;
  font-weight: 600;
`;

const BannerImage = styled.div`
  margin-top: 40px;
  img {
    width: 100%;
    max-width: 800px;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  }
`;

const PriceNote = styled.p`
  margin-top: 24px;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SisterCard = styled.div`
  background: #ffffff;
  border: 2px solid #f0f0f0;
  border-left: 5px solid #8ecf41;
  border-radius: 12px;
  padding: 40px;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  gap: 30px;
  align-items: flex-start;
  transition: all 0.3s ease;

  &:hover {
    border-color: #8ecf41;
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .emoji {
    font-size: 48px;
    line-height: 1;
  }

  h3 {
    font-size: 24px;
    color: #453793;
    margin: 10px 0;
    font-weight: 600;
  }

  p {
    font-size: 16px;
    line-height: 1.6;
    color: #666;
    margin: 0 0 12px;
  }

  p.note {
    font-size: 15px;
    color: #888;
    margin: 0;
  }

  a {
    color: #453793;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover {
    color: #5a4bb5;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 30px 20px;
    gap: 15px;
  }
`;

const SisterStatus = styled.span`
  display: inline-block;
  padding: 5px 14px;
  border-radius: 20px;
  background: rgba(142, 207, 65, 0.15);
  color: #5d8a22;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

export default function ZumoDoVideo() {
 /*   const uid = "8025df6276c8cb0e5f2dbfb985aa5e64";*/
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const screenshots = [
        { src: '/zumodo/images/Top3.png', alt: 'Top 3 Tasks View', caption: 'Pin your 3 must-dos. They stay front and center all day.' },
        { src: '/zumodo/images/Goals3.png', alt: 'Goals & Projects', caption: 'Break big goals into steps and check them off one by one' },
        { src: '/zumodo/images/Calendar3.png', alt: 'Calendar View', caption: 'Your calendar events and tasks in one view. Never miss what\'s next.' },
        //{ src: '/zumodo/images/NeedNew.png', alt: 'Projects Management', caption: 'Organize complex projects' },
        //{ src: '/zumodo/images/NeedNew.png', alt: 'Goal Details', caption: 'Break down goals into subtasks' },
/*        { src: '/zumodo/images/NeedNew.png', alt: 'Daily Habits', caption: 'Build healthy routines' },*/
      
    ];

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Call your Azure backend API
            const response = await fetch(`${API_BASE}/api/waitlist/ios`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    source: 'website'
                })
            });

            const data = await response.json();

            if (data.success) {
                setSubmitted(true);
                setEmail('');
            } else {
                alert(data.error || 'Failed to join waitlist. Please try again.');
            }
        } catch (error) {
            console.error('Waitlist submission error:', error);
            alert('Failed to join waitlist. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContainer>
            {/* HERO SECTION */}
            <HeroSection>
                <HeroContent>
                    <AppLogo>
                        <img src="/zumodo/images/ZumoDO_logo_sm.png" alt="ZumoDO Logo" />
                    </AppLogo>
                    <Tagline>Your day, organized by when you actually do things.</Tagline>
                    <Description>
                        Tasks, goals, habits, and calendar. Finally in one place. ZumoDO organizes your life by Morning, Afternoon, Prevening, and Evening so your to-do list matches how you actually live.
                    </Description>

                    <BadgeContainer>
                        <AppStoreBadge
                            href="https://play.google.com/store/apps/details?id=com.zumodo"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                alt="Get it on Google Play"
                            />
                        </AppStoreBadge>
                        <ComingSoonBadge>
                            🍎 iOS Coming Soon
                        </ComingSoonBadge>
                    </BadgeContainer>

                    <PriceNote>
                        Free for up to 14 days. Then $6.99 USD a month, or $59.99 USD a year.
                    </PriceNote>

                    {/* Banner image */}
                    <BannerImage>
                        <img src="/zumodo/images/ZumoDOBanner2.png" alt="ZumoDO App Preview" />
                    </BannerImage>
                </HeroContent>
            </HeroSection>

            {/* SCREENSHOTS SECTION */}
            <ContentSection>
                <SectionTitle>App Screenshots</SectionTitle>
                <SectionSubtitle>
                    Beautiful, intuitive design that makes productivity feel effortless
                </SectionSubtitle>

                <CarouselContainer>
                    <NavButton direction="left" onClick={prevSlide} aria-label="Previous screenshot">
                        ‹
                    </NavButton>

                    <CarouselWrapper>
                        <AnimatePresence mode="wait">
                            <Screenshot
                                key={currentSlide}
                                initial={{ opacity: 0, x: 100 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{ duration: 0.3 }}
                            >
                                <img
                                    src={screenshots[currentSlide].src}
                                    alt={screenshots[currentSlide].alt}
                                />
                            </Screenshot>
                        </AnimatePresence>
                    </CarouselWrapper>

                    <NavButton direction="right" onClick={nextSlide} aria-label="Next screenshot">
                        ›
                    </NavButton>

                    <ScreenshotCaption>
                        {screenshots[currentSlide].caption}
                    </ScreenshotCaption>

                    <DotsContainer>
                        {screenshots.map((_, index) => (
                            <Dot
                                key={index}
                                active={index === currentSlide}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to screenshot ${index + 1}`}
                            />
                        ))}
                    </DotsContainer>
                </CarouselContainer>
            </ContentSection>

            {/* VIDEO SECTION */}
            {/*<ContentSection>*/}
            {/*    <SectionTitle>See ZumoDO in Action</SectionTitle>*/}
            {/*    <SectionSubtitle>*/}
            {/*        Watch this 2-minute walkthrough to discover what makes ZumoDO different*/}
            {/*    </SectionSubtitle>*/}

            {/*    <VideoCard>*/}
            {/*        <VideoTitle>🎬 Product Walkthrough</VideoTitle>*/}
            {/*        <VideoSub>A quick tour of the app's key features and capabilities</VideoSub>*/}
            {/*        <FrameWrap>*/}
            {/*            <iframe*/}
            {/*                src={`https://iframe.videodelivery.net/${uid}?preload=true&controls=true`}*/}
            {/*                allow="accelerometer; autoplay; encrypted-media; picture-in-picture"*/}
            {/*                allowFullScreen*/}
            {/*                loading="lazy"*/}
            {/*                title="ZumoDO product video"*/}
            {/*            />*/}
            {/*        </FrameWrap>*/}
            {/*    </VideoCard>*/}
            {/*</ContentSection>*/}

            {/* FEATURES SECTION */}
            <ContentSection style={{ background: '#f5f5f5' }}>
                <SectionTitle>Everything You Need. Nothing You Don't.</SectionTitle>
                <SectionSubtitle>
                    ZumoDO brings together the tools that actually help you follow through. No complexity that slows you down.
                </SectionSubtitle>

                <FeaturesGrid>
                    <FeatureCard>
                        <div className="icon">🚩</div>
                        <h3>Top 3 Priorities</h3>
                        <p>
                            Each day, mark your 3 must-do tasks. ZumoDO keeps them front and center
                            so you always know what matters most.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">✅</div>
                        <h3>Smart Task Management</h3>
                        <p>
                            Create tasks with categories, due dates, repeating schedules, notes, and priority flags.
                            Search all your tasks instantly, or peek ahead at what's coming up.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">🎯</div>
                        <h3>Goals & Projects</h3>
                        <p>
                            Set ambitious goals with multiple subtasks, track your progress in real-time,
                            and celebrate when you close and archive completed projects.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">📅</div>
                        <h3>Calendar Integration</h3>
                        <p>
                            Seamlessly sync with your device calendars, create app events,
                            and manage calendar sources with custom colors.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">⏰</div>
                        <h3>Four Times of Day</h3>
                        <p>
                            Tasks are organized by when you actually do them: Morning, Afternoon, Prevening, and Evening. Prevening is that late-afternoon window, roughly 3 to 5 pm, when you have twenty minutes and no appetite for anything big. It is a great slot for quick wins, so it gets its own place instead of being buried at the end of the afternoon.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">⚡</div>
                        <h3>Focus & Pomodoro Timers</h3>
                        <p>
                            Every task card has a timer in the corner. Tap ⚡15 for a single
                            fifteen-minute countdown that takes over the screen, so there is nothing
                            to look at but the task and the clock. Tap 🍅 for a full Pomodoro cycle:
                            25 minutes of work, a 5-minute break, and a longer break after four
                            rounds. Pause, skip a phase, or close it whenever you like. Your phone
                            buzzes when time is up, and you choose how.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">📓</div>
                        <h3>Daily Journal</h3>
                        <p>
                            Three gratitudes in the morning, three things that went well at night.
                            Two minutes, and the research behind this kind of structured reflection
                            is genuinely good. Past entries stay, so you can look back on a week that
                            felt like nothing happened and find out otherwise.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">🧭</div>
                        <h3>Day Focus</h3>
                        <p>
                            Some days have a shape rather than a checklist. Name it: "Tuesday is cleaning and meal prep."
                            A Day Focus sits at the top of your Tasks screen on the days you choose, and nothing in it has
                            a checkbox, because the actual work already lives in your tasks. A goal asks how far along you
                            are. A Day Focus just answers what today is about.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">🔁</div>
                        <h3>Habits & Routines</h3>
                        <p>
                            Everything that repeats lives on its own tab. A habit is one behavior you're making automatic,
                            tracked as a streak on the days you pick. A routine is a named sequence of steps that collapses
                            into a single row, so a nine-step morning takes up one line instead of nine.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">🤖</div>
                        <h3>AI Assistant</h3>
                        <p>
                            Turn your thoughts into organized plans instantly. Just type naturally: "I need to prep for my presentation Tuesday and buy groceries this weekend." AI creates tasks, calendar events, habits, routines, and goals with dates, times, and categories. Say "urgent" and the task is automatically marked Important. Extra context you mention gets saved as a note. You review everything before it saves. 20 assists a month, and you can buy more if you run out. Powered by Azure OpenAI.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">🎨</div>
                        <h3>Color-Coded Categories</h3>
                        <p>
                            Organize tasks with color-coded categories: Self Care, Home, Work,
                            Financial, Food, Social, Pet, Auto & Transport, Education, Rest, Sleep, and Family.
                            Build your own, too, when your life needs a category we didn't think of.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">🔍</div>
                        <h3>Search Tasks</h3>
                        <p>
                            Find any task in seconds. Search across all your tasks by keyword, category, or date.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">📝</div>
                        <h3>Quick Notes</h3>
                        <p>
                            Somewhere to put a thought that is not a task yet. Color-code them,
                            reorder them, and turn one into a task with a tap when it becomes
                            something you actually have to do.
                        </p>
                    </FeatureCard>

                    <FeatureCard>
                        <div className="icon">↩️</div>
                        <h3>Oops Feature</h3>
                        <p>
                            Accidentally completed a task or deleted a note? Restore recently completed
                            tasks or deleted notes with one tap.
                        </p>
                    </FeatureCard>
                </FeaturesGrid>
            </ContentSection>

            {/* SISTER APP SECTION */}
            <ContentSection>
                <SectionTitle>A Sister App in Development</SectionTitle>
                <SectionSubtitle>
                    The 54321 nutrition checklist used to be a tab inside ZumoDO. It outgrew the app, so it is
                    becoming its own. ZumoDO stays focused on your day.
                </SectionSubtitle>

                <SisterCard>
                    <div className="emoji">🥦</div>
                    <div>
                        <SisterStatus>◎ In development</SisterStatus>
                        <h3>EAT 54321</h3>
                        <p>
                            Healthy eating made simple. 5 vegetables, 4 whole grains, 3 protein boosts, 2 fruits,
                            1 nuts or seeds. Every day, no calorie math. Just check the boxes. The weight, blood
                            pressure, sleep and mood logging that used to sit beside it moved across as well.
                        </p>
                        <p className="note">
                            A separate app from Zumosoft, not a ZumoDO feature.{' '}
                            <Link to="/contact">Get notified</Link> when it ships.
                        </p>
                    </div>
                </SisterCard>
            </ContentSection>

            {/* TECH STACK SECTION */}
            <ContentSection style={{ background: '#f5f5f5' }}>
                <TechStackSection>
                    <h3>Built with Modern Technology</h3>
                    <div className="stack-grid">
                        <div className="tech-badge">React Native</div>
                        <div className="tech-badge">Node.js / Express</div>
                        <div className="tech-badge">Azure Web App</div>
                        <div className="tech-badge">Azure Cosmos DB</div>
                        <div className="tech-badge">Azure OpenAI</div>
                        <div className="tech-badge">Firebase Auth</div>
                        <div className="tech-badge">Google Sign-In</div>
                        <div className="tech-badge">In-App Purchases</div>
                    </div>
                </TechStackSection>
            </ContentSection>

            {/* iOS WAITLIST SECTION */}
            <ContentSection>
                <WaitlistSection>
                    <h3>🍎 Join the iOS Waitlist</h3>
                    <p>
                        The iOS version is coming soon! Be the first to know when ZumoDO
                        launches on the Apple App Store. Sign up below to get notified.
                    </p>

                    {!submitted ? (
                        <WaitlistForm onSubmit={handleWaitlistSubmit}>
                            <EmailInput
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                            <SubmitButton type="submit" disabled={loading}>
                                {loading ? 'Submitting...' : 'Notify Me'}
                            </SubmitButton>
                        </WaitlistForm>
                    ) : (
                        <SuccessMessage>
                            ✓ You're on the list! We'll reach out when iOS launches.
                        </SuccessMessage>
                    )}
                </WaitlistSection>
            </ContentSection>
        </PageContainer>
    );
}
